import { lazy, type ComponentType } from 'react';

/**
 * Laster en del av appen først når den trengs.
 *
 * Forsiden, prislista og hjelpesidene er det en søkemotor og en som vurderer
 * tjenesten ser. De skal ikke betale for gjestelista, bordkartet og
 * administrasjonsportalen i tillegg – det er mesteparten av koden, og ingen
 * av det kjører før noen har logget inn. Vite legger alt som lastes slik i
 * egne filer, og nettleseren henter dem i det noen går inn i appen.
 *
 * **Det ene som kan gå galt, er en utrulling midt i et besøk.** Filnavnene
 * bærer en innholdshash, og en ny utgave har nye navn. En fane som ble åpnet
 * før utrullinga har den gamle `index.html` med de gamle navnene. Bildet tar
 * med seg filene fra de siste byggene (se «Filene fra tidligere utgaver» i
 * docs/azure.md), men ikke fra eldre, og ikke ved en tilbakerulling. Da finnes
 * ikke fila når fanen ber om appen – 404, og en side som ikke tegnes.
 * Da lastes siden på nytt én gang, så fanen får den nye utgaven. Bare én gang:
 * feiler det igjen, er det noe annet, og da skal feilgrensa få si det i stedet
 * for at siden laster i ring.
 *
 * Merket ligger i `sessionStorage`, som er per fane og borte når den lukkes.
 * Lagringen kan være stengt (privat modus), og da huskes det ikke – det
 * verste som skjer er at vi lar være å laste på nytt, og feilgrensa tar over.
 */
const MERKE = 'vaardag:lastet-paa-nytt';

function harLastetPaaNytt(): boolean {
  try {
    return sessionStorage.getItem(MERKE) === '1';
  } catch {
    // Vet ikke – og da gjetter vi ja, så vi aldri havner i en løkke.
    return true;
  }
}

function merkLastetPaaNytt(): void {
  try {
    sessionStorage.setItem(MERKE, '1');
  } catch {
    // Da huskes det ikke, og `harLastetPaaNytt` svarer ja neste gang uansett.
  }
}

function glemLastetPaaNytt(): void {
  try {
    sessionStorage.removeItem(MERKE);
  } catch {
    // Ingenting å glemme.
  }
}

/**
 * @param last Importen, for eksempel `() => import('./AppRuter')`. Modulen må
 *   ha en standardeksport; en navngitt eksport pakkes med
 *   `.then((m) => ({ default: m.Navn }))`.
 */
// `any` er det React selv skriver for `lazy`: komponenten kan ta hvilke
// egenskaper som helst, og det er typen på modulen som bærer dem videre.
export function lastDel<T extends ComponentType<any>>(last: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      const modul = await last();
      glemLastetPaaNytt();
      return modul;
    } catch (feil) {
      if (harLastetPaaNytt()) throw feil;
      merkLastetPaaNytt();
      window.location.reload();
      // Siden er på vei til å lastes på nytt. Et løfte som aldre løses, holder
      // React i «laster» til den er borte, i stedet for å vise en feil som
      // forsvinner om et halvt sekund.
      return new Promise<{ default: T }>(() => {});
    }
  });
}
